import { z } from 'zod';

// ============================================================================
// Multiplier Breakdown + Dynamic Price Response (P2-PRC-001)
// ============================================================================

export const MultiplierBreakdownSchema = z.object({
  capacityFactor: z.number(),
  dayOfWeekFactor: z.number(),
  seasonFactor: z.number(),
  demandFactor: z.number(),
  eventFactor: z.number(),
  ruleAdjustment: z.number(),
  rawMultiplier: z.number(),
  clampedMultiplier: z.number(),
  // 'LOCKED' = price was manually locked; 'UNKNOWN' = no ticket-type data available
  season: z.enum(['LOW', 'NORMAL', 'HIGH', 'PEAK', 'LOCKED', 'UNKNOWN']),
  capacityPercent: z.number().nullable(),
});

export type MultiplierBreakdown = z.infer<typeof MultiplierBreakdownSchema>;

export const DynamicPriceResultSchema = z.object({
  ticketTypeId: z.string(),
  ticketTypeName: z.string(),
  ticketTypeCode: z.string(),
  date: z.string(),
  basePrice: z.number().int().nonnegative(),
  multiplier: z.number().min(0.7).max(2.0),
  finalPrice: z.number().int().nonnegative(),
  currency: z.string(),
  taxIncluded: z.boolean(),
  isLocked: z.boolean(),
  multiplierBreakdown: MultiplierBreakdownSchema,
});

export type DynamicPriceResult = z.infer<typeof DynamicPriceResultSchema>;

export const TicketPriceSchema = z.object({
  ticketTypeId: z.string(),
  ticketTypeName: z.string(),
  basePrice: z.number().int().nonnegative(),
  multiplier: z.number(),
  finalPrice: z.number().int().nonnegative(),
  currency: z.string(),
  taxIncluded: z.boolean(),
  validFor: z.string(),
});

export type TicketPriceType = z.infer<typeof TicketPriceSchema>;

export const PriceMultiplierSchema = z.object({
  id: z.string(),
  ticketTypeId: z.string().nullable(),
  date: z.string().nullable(),
  dayOfWeek: z.number().int().min(0).max(6).nullable(),
  multiplier: z.number().min(0.7).max(2.0),
  reason: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type PriceMultiplier = z.infer<typeof PriceMultiplierSchema>;

// ============================================================================
// Query schemas for public calculation endpoints
// ============================================================================

export const CalculatePriceQuerySchema = z.object({
  ticketTypeId: z.string().min(1, 'ticketTypeId is required'),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be in YYYY-MM-DD format'),
});

export type CalculatePriceQuery = z.infer<typeof CalculatePriceQuerySchema>;

export const CalculateAllQuerySchema = z.object({
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be in YYYY-MM-DD format'),
});

export type CalculateAllQuery = z.infer<typeof CalculateAllQuerySchema>;

export const GetMultiplierQuerySchema = z.object({
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be in YYYY-MM-DD format'),
});

export type GetMultiplierQuery = z.infer<typeof GetMultiplierQuerySchema>;

// ============================================================================
// Weather response (GET /pricing/weather)
// ============================================================================

// ============================================================================
// Demand Level (used by pricing display and admin multiplier overview)
// ============================================================================

export const DEMAND_LEVELS = ['LOW', 'NORMAL', 'HIGH', 'SURGE'] as const;
export type DemandLevel = (typeof DEMAND_LEVELS)[number];
export const DemandLevelSchema = z.enum(DEMAND_LEVELS);

// ============================================================================
// Weather response (GET /pricing/weather)
// ============================================================================

export const WEATHER_CONDITIONS = ['SUNNY', 'CLOUDY', 'RAIN', 'STORM', 'UNKNOWN'] as const;
export type WeatherCondition = (typeof WEATHER_CONDITIONS)[number];

export const WeatherResponseSchema = z.object({
  condition: z.enum(WEATHER_CONDITIONS),
  weatherCode: z.string(),
  temperatureCelsius: z.number(),
  description: z.string(),
  fetchedAt: z.string(),
  cacheAgeSeconds: z.number().int().nullable(),
});

export type WeatherResponse = z.infer<typeof WeatherResponseSchema>;
